import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type Props = {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom';
  // Default tooltips are single-line (whitespace-nowrap). For longer
  // explanatory text pass `wide` to allow wrapping with a sensible max width.
  wide?: boolean;
};

// Minimum gap between the tooltip and any viewport edge. Keeps the tooltip
// from butting up against the screen border on mobile.
const VIEWPORT_MARGIN = 8;
// Gap between the trigger and the tooltip box.
const TRIGGER_GAP = 6;

// Portal-rendered tooltip so it can escape parents with overflow: hidden
// (e.g. card-flush). Position is computed from the trigger's bounding rect
// each time the tooltip opens, then clamped to the viewport so a trigger
// near a screen edge (common on mobile) doesn't push the tooltip offscreen.
export function Tooltip({ content, children, side = 'top', wide = false }: Props) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  // null until the layout pass has measured the tooltip and decided where to
  // park it. The portal renders with visibility:hidden in the meantime so the
  // first paint shows the tooltip already in its final position — no flash
  // at top:0/left:0.
  const [pos, setPos] = useState<{
    top: number;
    left: number;
    placement: 'top' | 'bottom';
  } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    if (!triggerRef.current || !tooltipRef.current) return;

    const trigger = triggerRef.current.getBoundingClientRect();
    const tooltip = tooltipRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Flip side when the preferred side would overflow vertically. The
    // tooltip's own height is included so we know the actual space it needs
    // in either direction. If neither side fits we honor the preferred side
    // and let the y-clamp below pin it inside the viewport.
    let placement: 'top' | 'bottom' = side;
    const fitsTop = trigger.top - TRIGGER_GAP - tooltip.height >= VIEWPORT_MARGIN;
    const fitsBottom =
      trigger.bottom + TRIGGER_GAP + tooltip.height <= vh - VIEWPORT_MARGIN;
    if (side === 'top' && !fitsTop && fitsBottom) placement = 'bottom';
    if (side === 'bottom' && !fitsBottom && fitsTop) placement = 'top';

    // Top: anchored to the trigger's top edge with a gap, then the tooltip
    // is shifted up by its full height via the transform below.
    // Bottom: anchored to the trigger's bottom edge with a gap, no shift.
    let top =
      placement === 'top'
        ? trigger.top - TRIGGER_GAP - tooltip.height
        : trigger.bottom + TRIGGER_GAP;
    // Final y-clamp in case neither side has room (e.g. trigger taller than
    // the viewport, or zoomed-in mobile). Keeps the tooltip onscreen even if
    // it has to slightly overlap the trigger.
    top = Math.max(
      VIEWPORT_MARGIN,
      Math.min(top, vh - tooltip.height - VIEWPORT_MARGIN),
    );

    // Center horizontally on the trigger, then clamp so the tooltip's
    // visible left/right edges stay inside the viewport with a small
    // margin. This is what keeps tooltips near the right edge of the
    // screen from running off on mobile. When the tooltip is wider than
    // the viewport-minus-margins (extreme case), we just pin its left edge
    // to the margin so the start of the content is readable.
    let left = trigger.left + trigger.width / 2 - tooltip.width / 2;
    const minLeft = VIEWPORT_MARGIN;
    const maxLeft = vw - tooltip.width - VIEWPORT_MARGIN;
    if (maxLeft >= minLeft) {
      left = Math.max(minLeft, Math.min(left, maxLeft));
    } else {
      left = minLeft;
    }

    setPos({ top, left, placement });
  }, [open, side, content, wide]);

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center"
      >
        {children}
      </span>
      {open &&
        createPortal(
          <span
            ref={tooltipRef}
            role="tooltip"
            className={
              'fixed z-50 px-2 py-1 rounded-md bg-elevated border border-border-strong text-xs text-fg shadow-lg pointer-events-none ' +
              (wide ? 'max-w-xs leading-snug' : 'whitespace-nowrap')
            }
            style={{
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              // Hidden on the first frame so the measurement pass can run
              // before the tooltip is visible to the user — avoids the
              // top-left flash.
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            {content}
          </span>,
          document.body,
        )}
    </>
  );
}
